import { ShopifyCartResponse, ShopifyProductResponse } from './types';

/**
 * Response object for when a HTTP request succeeds.
 */
interface SuccessResponse<T> {
  success: true;
  statusCode: number;
  body: T;
}

/**
 * Response object for when a HTTP request fails.
 *
 * This includes errors that can be either:
 * 1. JSON Parse Failures.
 * 2. Bad HTTP status codes.
 * 3. Network failures.
 */
interface FailureResponse {
  success: false;
  statusCode: number;
  error: Error;
  body?: object|object[];
}

const defaultRequestError = new Error("Failed to fetch shopify endpoint.");

/**
 * Response type for Shopify API request.
 */
export type ShopifyResponse<T> = SuccessResponse<T> | FailureResponse;

/**
 * The HTTP Client for shopify.
 */
class Client {
  /**
   * API Client to make HTTP GET requests to Shopify's /cart.js API.
   *
   * @param url The URL to request.
   */
  private async request<T>(url: string): Promise<ShopifyResponse<T>> {
    try {
      const response = await fetch(url);
      const body = await response.json();

      // ensure we have a successful response
      if (response.ok) {
        return {
          success: true,
          statusCode: response.status,
          body: body
        };
      }

      return {
        success: false,
        statusCode: response.status,
        body: body,
        error: defaultRequestError
      };
    } catch (e) {
      return {
        success: false,
        statusCode: -1,
        error: e,
      };
    }
  }

  /**
   * Request to fetch the shopify /cart.js data.
   */
  public async getCart(): Promise<ShopifyResponse<ShopifyCartResponse>> {
    return this.request("/cart.js");
  }

  public async getProduct(productHandle: string): Promise<ShopifyResponse<ShopifyProductResponse>> {
    return this.request(`/products/${productHandle}.js`);
  }
}

/**
 * There should only ever be one HTTP client.
 */
const ApiClient = new Client();
export default ApiClient;
